import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "./ui.js";

// Type-to-filter keyword picker. Same UX as ProjectPicker/KocPicker but works on a flat
// list of strings (e.g. unique monitor_keywords aggregated across projects). The selected
// value is the keyword string itself.
export function KeywordPicker({
  keywords,
  value,
  onChange,
  placeholder = "search keyword…",
}: {
  keywords: string[];
  value: string | null | undefined;
  onChange: (keyword: string | null) => void;
  placeholder?: string;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = value && value.length > 0 ? value : null;

  useEffect(() => {
    if (selected) setSearch("");
  }, [selected]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const matches = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return keywords.slice(0, 50);
    return keywords.filter((k) => k.toLowerCase().includes(s)).slice(0, 50);
  }, [search, keywords]);

  const displayValue = selected ?? search;

  return (
    <div className="relative flex-1" ref={wrapRef}>
      <Input
        value={displayValue}
        placeholder={placeholder}
        onChange={(e) => {
          if (selected) onChange(null);
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {selected && (
        <button
          type="button"
          aria-label="clear keyword"
          onClick={() => { onChange(null); setSearch(""); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-sm leading-none"
        >
          ×
        </button>
      )}
      {open && (
        <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border border-stone-300 bg-white shadow-lg text-sm">
          {matches.length === 0 ? (
            <li className="px-2 py-2 text-xs text-stone-500">
              No keyword matches “{search}”.
            </li>
          ) : (
            matches.map((k) => (
              <li
                key={k}
                className="flex items-center px-2 py-1.5 hover:bg-stone-100 cursor-pointer"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(k);
                  setSearch("");
                  setOpen(false);
                }}
              >
                <span className="truncate">{k}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
